import { useState, useMemo } from 'react';
import { demoBookings, demoPayments, demoCanteenBills } from '@/mocks/demoData';
import { getActiveShift } from '@/utils/shiftStore';

export default function NightAudit() {
  const today = new Date().toISOString().slice(0, 10);
  const [auditDate, setAuditDate] = useState(today);

  const stats = useMemo(() => {
    const dateBookings = demoBookings.filter((b) => {
      return new Date(b.check_in).toISOString().slice(0, 10) <= auditDate && new Date(b.check_out).toISOString().slice(0, 10) >= auditDate;
    });
    const dateCheckins = demoBookings.filter((b) => new Date(b.check_in).toISOString().slice(0, 10) === auditDate && (b.status === 'checked_in' || b.status === 'checked_out'));
    const dateCheckouts = demoBookings.filter((b) => new Date(b.check_out).toISOString().slice(0, 10) === auditDate && b.status === 'checked_out');
    const datePayments = demoPayments.filter((p) => new Date(p.created_at).toISOString().slice(0, 10) === auditDate);
    const dateCanteen = demoCanteenBills.filter((b) => new Date(b.created_at).toISOString().slice(0, 10) === auditDate);

    const roomRevenue = demoBookings
      .filter((b) => new Date(b.check_out).toISOString().slice(0, 10) === auditDate && b.status === 'checked_out')
      .reduce((s, b) => s + b.total_amount, 0);

    const totalPayments = datePayments.reduce((s, p) => s + p.amount, 0);
    const totalCanteen = dateCanteen.filter((b) => b.status === 'paid').reduce((s, b) => s + b.total, 0);
    const totalCanteenPending = dateCanteen.filter((b) => b.status === 'pending').reduce((s, b) => s + b.total, 0);

    const paymentMethods: Record<string, number> = {};
    datePayments.forEach((p) => { paymentMethods[p.payment_method] = (paymentMethods[p.payment_method] || 0) + p.amount; });

    const occupancy = dateBookings.filter((b) => b.status === 'checked_in').length;

    const activeShift = getActiveShift();

    return {
      dateCheckins: dateCheckins.length,
      dateCheckouts: dateCheckouts.length,
      roomRevenue,
      totalPayments,
      totalCanteen,
      totalCanteenPending,
      paymentMethods,
      occupancy,
      activeShift,
      totalRevenue: roomRevenue + totalCanteen,
      uncollected: dateCheckins.filter((b) => b.pending_amount > 0).reduce((s, b) => s + b.pending_amount, 0),
    };
  }, [auditDate]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-navy-800">Night Audit</h1>
          <p className="text-navy-500 mt-1">End-of-day financial reconciliation and daily summary report.</p>
        </div>
        <input type="date" value={auditDate} onChange={(e) => setAuditDate(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Room Revenue</div>
          <div className="text-xl font-bold text-navy-800">Rs. {stats.roomRevenue.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Canteen Revenue</div>
          <div className="text-xl font-bold text-navy-800">Rs. {stats.totalCanteen.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-green-200 p-4">
          <div className="text-xs text-navy-500 mb-1">Total Revenue</div>
          <div className="text-xl font-bold text-green-600">Rs. {stats.totalRevenue.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-red-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Uncollected</div>
          <div className="text-xl font-bold text-red-600">Rs. {stats.uncollected.toLocaleString('en-IN')}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Operational Summary */}
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <h3 className="font-semibold text-navy-800 mb-4">Operational Summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-navy-50">
              <span className="text-sm text-navy-500">Check-ins</span>
              <span className="font-medium text-navy-800">{stats.dateCheckins}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-navy-50">
              <span className="text-sm text-navy-500">Check-outs</span>
              <span className="font-medium text-navy-800">{stats.dateCheckouts}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-navy-50">
              <span className="text-sm text-navy-500">Current Occupancy</span>
              <span className="font-medium text-navy-800">{stats.occupancy} rooms</span>
            </div>
            <div className="flex justify-between py-2 border-b border-navy-50">
              <span className="text-sm text-navy-500">Active Shift</span>
              <span className="font-medium text-navy-800">{stats.activeShift?.name || 'None'}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-navy-50">
              <span className="text-sm text-navy-500">Shift Collections</span>
              <span className="font-medium text-navy-800">Rs. {(stats.activeShift?.collections || 0).toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-navy-50">
              <span className="text-sm text-navy-500 pl-4 text-navy-400">Room Payments</span>
              <span className="font-medium text-navy-700">Rs. {((stats.activeShift?.collections || 0) - (stats.activeShift?.canteen_collections || 0)).toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-navy-50">
              <span className="text-sm text-navy-500 pl-4 text-teal-600">Canteen Collections</span>
              <span className="font-medium text-teal-600">Rs. {(stats.activeShift?.canteen_collections || 0).toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-sm text-navy-500">Canteen Pending Charges</span>
              <span className="font-medium text-amber-600">Rs. {stats.totalCanteenPending.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>

        {/* Payment Method Breakdown */}
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <h3 className="font-semibold text-navy-800 mb-4">Payment Method Breakdown</h3>
          <div className="space-y-3">
            {Object.entries(stats.paymentMethods).length > 0 ? (
              Object.entries(stats.paymentMethods)
                .sort((a, b) => b[1] - a[1])
                .map(([method, amount]) => (
                  <div key={method} className="flex justify-between py-2 border-b border-navy-50">
                    <span className="text-sm text-navy-500 capitalize">{method.replace('_', ' ')}</span>
                    <span className="font-medium text-navy-800">Rs. {amount.toLocaleString('en-IN')}</span>
                  </div>
                ))
            ) : (
              <div className="text-center py-6 text-navy-400 text-sm">No payments for this date</div>
            )}
            <div className="flex justify-between py-2 pt-2 border-t-2 border-navy-200">
              <span className="font-semibold text-navy-800">Total Collections</span>
              <span className="font-bold text-navy-800">Rs. {stats.totalPayments.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="bg-white rounded-xl border border-navy-100 p-5 mb-6">
        <h3 className="font-semibold text-navy-800 mb-4">Revenue Breakdown</h3>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-navy-600">Room Revenue</span>
              <span className="font-medium text-navy-800">Rs. {stats.roomRevenue.toLocaleString('en-IN')}</span>
            </div>
            <div className="w-full bg-navy-100 rounded-full h-2.5">
              <div className="bg-navy-600 h-2.5 rounded-full" style={{ width: `${stats.totalRevenue > 0 ? (stats.roomRevenue / stats.totalRevenue) * 100 : 0}%` }} />
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-navy-600">Canteen Revenue</span>
              <span className="font-medium text-navy-800">Rs. {stats.totalCanteen.toLocaleString('en-IN')}</span>
            </div>
            <div className="w-full bg-navy-100 rounded-full h-2.5">
              <div className="bg-gold-500 h-2.5 rounded-full" style={{ width: `${stats.totalRevenue > 0 ? (stats.totalCanteen / stats.totalRevenue) * 100 : 0}%` }} />
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-navy-600">Uncollected</span>
              <span className="font-medium text-red-600">Rs. {stats.uncollected.toLocaleString('en-IN')}</span>
            </div>
            <div className="w-full bg-navy-100 rounded-full h-2.5">
              <div className="bg-red-400 h-2.5 rounded-full" style={{ width: `${stats.totalRevenue > 0 ? (stats.uncollected / (stats.totalRevenue + stats.uncollected)) * 100 : 0}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* Audit Status */}
      <div className="bg-white rounded-xl border border-navy-100 p-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
            <i className="ri-check-double-line text-green-600 text-xl" />
          </div>
          <div>
            <h3 className="font-semibold text-navy-800">Audit Status: {auditDate}</h3>
            <p className="text-sm text-navy-500">
              {auditDate === today ? 'Today\'s audit is in progress. All transactions are being tracked.' : `Audit data for ${new Date(auditDate).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}.`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}